import { PageHeader, LoadingState, DataTable } from '../components/AdminPageUI';
import StudentAvatar from '../components/StudentAvatar';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, TextField, MenuItem, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Chip } from '@mui/material';
import { Search as SearchIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

export default function TransportEnrollmentPage() {
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [terms, setTerms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  
  // Selection State
  const [selectedTermId, setSelectedTermId] = useState('');
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear().toString());
  const [routeFilter, setRouteFilter] = useState('');
  
  // Enrollment Form State
  const [studentSearch, setStudentSearch] = useState('');
  const [studentSearchResults, setStudentSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedStop, setSelectedStop] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      let schoolId = '';
      if (userStr) {
        const user = JSON.parse(userStr);
        schoolId = user.school?._id || user.school || '';
      }

      const [routeRes, termRes] = await Promise.all([
        api.get('/transport/routes'),
        api.get('/term/all', { params: { school: schoolId } })
      ]);
      
      const fetchedTerms = termRes.data.allTerms || [];
      setRoutes(routeRes.data.routes || []);
      setTerms(fetchedTerms);
      if (fetchedTerms.length > 0) {
        setSelectedTermId(fetchedTerms[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load initial data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    if (!selectedTermId) return;
    try {
      const res = await api.get('/transport/enrollments', {
        params: { termId: selectedTermId, routeId: routeFilter }
      });
      setEnrollments(res.data.enrollments || []);
    } catch (err) {
      toast.error('Failed to fetch enrollments');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTermId) {
      fetchEnrollments();
    }
  }, [selectedTermId, routeFilter]);

  const handleSearchStudent = async () => {
    if (!studentSearch.trim()) return;
    try {
      // Using existing search endpoint if it exists, otherwise a generic student fetch
      const res = await api.get('/students');
      const filtered = res.data.filter(s => 
        (s.name || '').toLowerCase().includes(studentSearch.toLowerCase()) || 
        (s.admissionNumber || '').includes(studentSearch)
      ).slice(0, 5); // take top 5
      setStudentSearchResults(filtered);
    } catch (err) {
      toast.error('Failed to search students');
    }
  };

  const selectedRouteObj = routes.find(r => r._id === selectedRouteId);

  const handleEnroll = async () => {
    if (!selectedStudent || !selectedTermId || !selectedRouteId || !selectedStop) {
      toast.warning('Please complete all enrollment fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      await api.post('/transport/enrollments', {
        studentId: selectedStudent._id,
        termId: selectedTermId,
        academicYear,
        routeId: selectedRouteId,
        stop: selectedStop
      });
      toast.success('Student successfully enrolled in transport!');
      
      // Reset form
      setSelectedStudent(null);
      setStudentSearchResults([]);
      setStudentSearch('');
      setSelectedRouteId('');
      setSelectedStop('');
      
      // Refresh table
      fetchEnrollments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to enroll student');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <LoadingState />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={<>Transport Enrollment</>} description={<>Enroll students on routes and manage their transport access.</>}></PageHeader>

      <Grid container spacing={3}>
        {/* Left Side: Enrollment Form */}
        <Grid size={{ xs: 12, md: 4 }}   >
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Enroll a Student</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search Student Name/ID"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchStudent()}
                  />
                  <IconButton color="primary" onClick={handleSearchStudent}>
                    <SearchIcon />
                  </IconButton>
                </Box>

                {studentSearchResults.length > 0 && !selectedStudent && (
                  <Paper className="workspace-section" variant="outlined" sx={{ p: 1, maxHeight: 150, overflowY: 'auto' }}>
                    {studentSearchResults.map(student => (
                      <Box 
                        key={student._id} 
                        onClick={() => setSelectedStudent(student)}
                        sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <StudentAvatar student={student} sx={{ width: 24, height: 24 }} />
                        <Typography variant="body2">{student.name} ({student.admissionNumber})</Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                {selectedStudent && (
                  <Paper className="workspace-section" variant="outlined" sx={{ p: 2, bgcolor: 'primary.50', borderColor: 'primary.main', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" color="primary.main">Selected Student:</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedStudent.name}</Typography>
                      <Typography variant="caption">{selectedStudent.admissionNumber}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setSelectedStudent(null)}><DeleteIcon fontSize="small" /></IconButton>
                  </Paper>
                )}

                <TextField
                  select
                  size="small"
                  label="Academic Term"
                  value={selectedTermId}
                  onChange={(e) => setSelectedTermId(e.target.value)}
                >
                  {terms.map(t => (
                    <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Transport Route"
                  value={selectedRouteId}
                  onChange={(e) => {
                    setSelectedRouteId(e.target.value);
                    setSelectedStop(''); // reset stop when route changes
                  }}
                >
                  {routes.map(r => (
                    <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
                  ))}
                </TextField>

                {selectedRouteObj && (
                  <TextField
                    select
                    size="small"
                    label="Drop-off Stop"
                    value={selectedStop}
                    onChange={(e) => setSelectedStop(e.target.value)}
                  >
                    {selectedRouteObj.stops.map((stop, i) => (
                      <MenuItem key={i} value={stop}>{i + 1}. {stop}</MenuItem>
                    ))}
                  </TextField>
                )}

                <Button 
                  variant="contained" 
                  size="large"
                  disabled={isSubmitting || !selectedStudent || !selectedRouteId || !selectedStop}
                  onClick={handleEnroll}
                  sx={{ mt: 2 }}
                >
                  {isSubmitting ? 'Enrolling...' : 'Confirm Enrollment'}
                </Button>

              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Enrolled Students Table */}
        <Grid size={{ xs: 12, md: 8 }}   >
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">Current Enrollments</Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="Filter by Route"
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value)}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="">All Routes</MenuItem>
                    {routes.map(r => (
                      <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>

              <DataTable component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'background.default' }}>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Route</TableCell>
                      <TableCell>Drop-off Stop</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enrollments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">No students enrolled in this view.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      enrollments.map((enr) => (
                        <TableRow key={enr._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {enr.student?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {enr.student?.admissionNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{enr.route?.name}</TableCell>
                          <TableCell>
                            <Chip label={enr.dropOffStop} size="small" variant="outlined" color="primary" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label="Enrolled" size="small" color="success" />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </DataTable>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
